import { Link, useLocation } from '@tanstack/react-router';
import { ArrowLeft, Compass, LayoutGrid } from 'lucide-react';
import { useEngagementQuery } from '@/lib/api/engagements';
import { cn } from '@/lib/utils/cn';

/**
 * The page for an address that matches no screen (C2-46). Replaces the
 * router's bare "Not Found": it says what happened in plain words and offers
 * the way back — to the engagement when the address is under one (named,
 * once the engagement loads), and always to the portfolio.
 *
 * Mounted as the root route's `notFoundComponent` and as the router's
 * `defaultNotFoundComponent`, so a miss inside an engagement layout renders
 * it too, within the engagement's chrome.
 */

const ENGAGEMENT_PATH_RE =
  /^\/engagements\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

/** The engagement an address sits under, or null. */
export function engagementIdFromPath(pathname: string): string | null {
  return ENGAGEMENT_PATH_RE.exec(pathname)?.[1] ?? null;
}

const LINK_BASE =
  'inline-flex h-9 max-w-full items-center gap-1.5 rounded-card px-3.5 text-body-sm font-medium no-underline transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:ring-offset-1';

export function NotFound() {
  const { pathname } = useLocation();
  const engagementId = engagementIdFromPath(pathname);
  const engagement = useEngagementQuery(engagementId ?? undefined);
  // An engagement that doesn't exist (or isn't the reader's) gets no
  // "back" link: it would lead to another dead end.
  const showEngagementLink = engagementId !== null && !engagement.isError;
  const engagementName = engagement.data?.name ?? null;

  return (
    <section
      aria-labelledby="not-found-title"
      className="mx-auto my-12 flex max-w-[560px] flex-col items-center rounded-card border border-ink-100 bg-paper-0 px-6 py-10 text-center"
    >
      <span
        aria-hidden="true"
        className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-paper-100 text-ink-500"
      >
        <Compass className="h-5 w-5" />
      </span>
      <p className="mt-4 text-meta font-semibold uppercase tracking-[0.06em] text-accent-amber-700">
        Page not found
      </p>
      <h1 id="not-found-title" className="mt-1 text-h2 font-semibold text-ink-900">
        There is no page at this address
      </h1>
      <p className="mt-2 text-body-sm text-ink-500">
        The link may be mistyped or out of date, or the page may have moved.
        {showEngagementLink
          ? ' Go back to the engagement, or to the portfolio of your engagements.'
          : ' Go back to the portfolio of your engagements.'}
      </p>
      <div className="mt-6 flex max-w-full flex-wrap items-center justify-center gap-2">
        {showEngagementLink ? (
          <Link
            to="/engagements/$engagementId"
            params={{ engagementId }}
            className={cn(
              LINK_BASE,
              'bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700 hover:text-paper-0',
            )}
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {engagementName ? `Back to ${engagementName}` : 'Back to the engagement'}
            </span>
          </Link>
        ) : null}
        <Link
          to="/"
          className={cn(
            LINK_BASE,
            showEngagementLink
              ? 'border border-ink-200 bg-paper-0 text-ink-700 hover:bg-paper-100 hover:text-ink-900'
              : 'bg-accent-amber-600 text-paper-0 hover:bg-accent-amber-700 hover:text-paper-0',
          )}
        >
          <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden="true" />
          Go to the portfolio
        </Link>
      </div>
    </section>
  );
}
